import { Address, AvailableBookingTimeWindow, BookingProposalItem } from 'noddi-types';
import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

export interface BookingProposalStoreProps {
  address?: Address;
  bookingTimeWindow?: AvailableBookingTimeWindow;
  bookingProposalItems: BookingProposalItem[];
  slug: string;
}

interface BookingProposalState extends BookingProposalStoreProps {
  actions: {
    setAddress: (address?: Address) => void;
    setBookingTimeWindow: (bookingTimeWindow?: AvailableBookingTimeWindow) => void;
  };
  hasHydrated?: boolean;
}

export type BookingProposalStore = ReturnType<typeof createBookingProposalStore>;

export const createBookingProposalStore = (initProps: BookingProposalStoreProps) =>
  createStore<BookingProposalState>()(
    devtools(
      persist(
        (set) => ({
          ...initProps,
          actions: {
            setAddress: (address) => set({ address }),
            setBookingTimeWindow: (bookingTimeWindow) => set({ bookingTimeWindow })
          }
        }),
        {
          name: 'booking-proposal-store',
          storage: createJSONStorage(() => sessionStorage),
          partialize: (state) => ({
            address: state.address,
            bookingTimeWindow: state.bookingTimeWindow,
            bookingProposalItems: state.bookingProposalItems,
            slug: state.slug
          })
        }
      )
    )
  );

export const BookingProposalContext = createContext<BookingProposalStore | null>(null);

function useBookingProposalContext<T>(selector: (state: BookingProposalState) => T): T {
  const store = useContext(BookingProposalContext);
  if (!store) {
    throw new Error('Missing BookingProposalContext.Provider in the tree');
  }
  return useStore(store, selector);
}

export const useBookingProposalAddress = () => useBookingProposalContext((state) => state.address);
export const useBookingProposalSlug = () => useBookingProposalContext((state) => state.slug);
export const useBookingProposalTimeWindow = () => useBookingProposalContext((state) => state.bookingTimeWindow);
export const useBookingProposalItems = () => useBookingProposalContext((state) => state.bookingProposalItems);

export const useBookingProposalActions = () => useBookingProposalContext((state) => state.actions);
